"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GallerySlider from "@/components/Auctions/AuctionItem/GallerySlider";
import { Auction, AuctionItem } from "@/types/auction";
import {
  checkIfAuctionItemEnded,
  BID_TIME_REMAINING_COUNTDOWN,
  REVALIDATE_BID_TIME,
  REVALIDATE_AUCTION_ITEM_TIME,
} from "@/lib/auctions";
import useConfirmedEnded from "@/hooks/useConfirmedEnded";
import { getBidsByAuctionItemId } from "@/actions/bids";
import AuctionBidHistory from "@/components/Auctions/AuctionBidHistory";
import AuctionBidField from "@/components/Auctions/AuctionBidField";
import useSWR, { mutate } from "swr";
import NoImageFound from "@/components/NoImageFound";
import useUserStore from "@/stores/useUserStore";
import ReadMoreArea from "@/components/Plugins/ReadMoreArea";
import Link from "next/link";
import { getAuctionItemLink, getAuctionLink } from "@/lib/links";
import { useShallow } from "zustand/react/shallow";
import useAuctionItemFavorite from "@/hooks/useAuctionItemFavorite";
import AuctionItemFAQs from "@/components/Auctions/AuctionItem/AuctionItemFaqs";
import TopbarAuctionPagination from "@/components/Auctions/AuctionItem/TopbarAuctionPagination";
import classNames from "classnames";
import { useSearchParams, useRouter } from "next/navigation";
import { formatPrice, parseDate } from "@/lib/utils";
import useServerTime from "@/hooks/useServerTime";
import Skeleton from "react-loading-skeleton";
import FavoriteButton from "@/components/Auctions/AuctionItem/FavoriteButton";
import BidCountDown from "@/components/Auctions/AuctionItem/BidCountDown";
import CurrentBid from "@/components/Auctions/AuctionItem/CurrentBid";
import useLiveBids from "@/components/WebSocket/LiveBidsSocket";
import { SocketProvider } from "@/components/WebSocket/SocketProvider";
import useBidStore from "@/stores/useBidStore";
import Finance from "@/components/Finance";
import useModalStore from "@/hooks/useModalStore";
import Button from "@/components/Elements/Button";
import ActiveAuctionTimeSync from "@/components/ActiveAuctionTimeSync";
import "react-loading-skeleton/dist/skeleton.css";
import "moment/locale/sv";
import "swiper/css";
import "swiper/css/pagination";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

interface AuctionProps {
  item: AuctionItem;
  auction: Auction;
  cachedBids: any;
  themeSettings: any;
  sortedItemsForPagination: any;
}

const MAX_BIDS_FETCHED = 10;

const AuctionItemClient: React.FC<AuctionProps> = ({
  item: getCachedAuctionItem,
  sortedItemsForPagination,
  auction,
  cachedBids,
  themeSettings,
}) => {
  const serverTime = useServerTime();
  const { loginModal } = useModalStore();

  const [bidData, setBidData] = useState<any>(null);
  const [showAllBids, setSHowAllBids] = React.useState(false);
  const [item, setAuctionItem] = useState(getCachedAuctionItem);
  const [useFetchSeed, setUseFetchSeed] = useState(true);
  const [fetchSeed, setFetchSeed] = useState(() => Math.random().toString());
  const { user, userInfo } = useUserStore(useShallow((state) => state));
  const { bidFetchKeySeed } = useBidStore();
  const [leadingAutoBidUpdated, setLeadingAutoBidUpdated] = useState<any>(null);

  const [fetchCount, setFetchCount] = useState(0);
  const [bidsDataCount, setBidsDataCount] = useState(0);
  const [hasFetchedEndedBids, setHasFetchedEndedBids] = useState(false);

  const { auctionItemBidsLive, mergeWithInitialBids } = useLiveBids({
    auctionItemIds: [item.itemId],
  });

  const itemLink = getAuctionItemLink(item);
  const searchParams = useSearchParams();
  const auctionItemFavorite = useAuctionItemFavorite({
    itemId: getCachedAuctionItem?.itemId,
    userId: user?.id,
  });

  const pollRef = useRef(() => auctionItemFetcher());
  useEffect(() => { pollRef.current = () => auctionItemFetcher(); });

  const auctionHasEnded = auction?.partStatus === "ended";

  const handleEndedPoll = useCallback(() => pollRef.current(), []);
  const hasEnded = useConfirmedEnded(item?.itemEndTime, serverTime, handleEndedPoll);

  const getSearchRef = searchParams?.get("ref");

  const fetchKey = useFetchSeed
    ? ["/api/getItemBidsAndAutionItem", item.itemId, showAllBids, fetchSeed]
    : ["/api/getItemBidsAndAutionItem", item.itemId, showAllBids];

  useEffect(() => {
    if (typeof window === "undefined") return;

    sessionStorage.setItem(
      `lastViewedItem-${auction.partId}`,
      String(item.itemId)
    );
  }, [auction.partId, item.itemId]);

  useEffect(() => {
    if (!getSearchRef) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("ref");
    window.history.replaceState(null, "", url.toString());
  }, [getSearchRef]);

  useEffect(() => {
    // Reset fetchSeed when item changes to bypass cache on first load
    setFetchSeed(Math.random().toString());
    setUseFetchSeed(true);
    //mutate(fetchKey, undefined, { revalidate: true });
  }, [item.itemId, bidFetchKeySeed]);

  useEffect(() => {
    if (auctionItemBidsLive?.length > 0 && bidData) {
      const getLeadingAutoBid = () => {
        // Find the live item by itemId (string or number)
        const liveItem = auctionItemBidsLive.find(
          (entry: any) => String(entry.itemId) === String(item.itemId)
        );
        return liveItem?.leadingAutoBid || bidData.leadingAutoBid;
      };

      const bidDataWrapper = {
        itemId: item.itemId,
        bids: bidData.bids || [],
        itemEndTime: bidData.itemEndTime,
        serverTime: bidData.serverTime,
        leadingAutoBid: getLeadingAutoBid(),
      };

      const mergedItems = mergeWithInitialBids([bidDataWrapper]);
      const updatedBidData = mergedItems[0];

      if (
        updatedBidData &&
        updatedBidData.bids?.length > bidData.bids?.length
      ) {
        setBidData({
          ...bidData,
          bids: updatedBidData.bids,
          itemEndTime: updatedBidData.itemEndTime,
          serverTime: updatedBidData.serverTime,
          leadingAutoBid: updatedBidData.leadingAutoBid,
        });

        setAuctionItem((prev: any) => ({
          ...prev,
          itemEndTime: updatedBidData.itemEndTime || prev.itemEndTime,
          itemHasEnded: checkIfAuctionItemEnded(
            updatedBidData.itemEndTime || prev.itemEndTime,
            serverTime
          ),
        }));
      } else if (
        JSON.stringify(bidData.leadingAutoBid) !=
        JSON.stringify(getLeadingAutoBid())
      ) {
        setBidData((prev: any) => ({
          ...prev,
          leadingAutoBid: getLeadingAutoBid(),
        }));
      }
    }
  }, [auctionItemBidsLive, item.itemId, bidData, serverTime]);

  // Failsafe extra polling in case SWR refreshInterval is suppressed (e.g. background tabs)
  useEffect(() => {
      if (auctionHasEnded) return;

      const id = setInterval(() => {
          pollRef.current()
      }, 10000);

      const onVisibilityChange = () => {
          if (document.visibilityState === "visible") {
              pollRef.current()
          }
      };
      document.addEventListener("visibilitychange", onVisibilityChange);

      return () => {
          clearInterval(id);
          document.removeEventListener("visibilitychange", onVisibilityChange);
      };
  }, [auctionHasEnded]);

  const auctionItemFetcher = async () => {
    if (auctionHasEnded) {
      // For recently ended items (< 48h), fetch fresh bids once to avoid stale cached data
      const itemEndTimestamp = parseDate(item.itemEndTime);
      const twoDaysMs = 2 * 24 * 60 * 60 * 1000;
      const endedRecently = Date.now() - itemEndTimestamp < twoDaysMs;

      if (endedRecently && !hasFetchedEndedBids) {
        setHasFetchedEndedBids(true);
        const freshBids = await getBidsByAuctionItemId(item.itemId, 0, Date.now().toString());
        if (freshBids?.bids?.length > 0) {
          setBidData(freshBids);
          setAuctionItem((prev) => ({
            ...prev,
            itemHasBeenSynced: true,
          }));
          return true;
        }
      }

      setAuctionItem((prev) => ({
        ...prev,
        itemHasBeenSynced: true,
      }));
      
      if (!hasFetchedEndedBids) {
        setBidData(cachedBids);
      }
      return true;
    }

    const bidsData = await getBidsByAuctionItemId(item.itemId, 0, (useFetchSeed ? fetchSeed : ""));

    // Always update leading autobid from fetched data
    setLeadingAutoBidUpdated(bidsData?.leadingAutoBid ? bidsData.leadingAutoBid : false);

    if (bidsData) {
      setBidData((prev: any) => {
        if (!prev) return bidsData;
        // Keep whichever has more bids (WebSocket may have already merged newer ones)
        setFetchCount((prev) => prev + 1);
        setBidsDataCount(bidsData.bids?.length);

        return bidsData.bids?.length >= prev.bids?.length ? bidsData : prev;
      });

      setUseFetchSeed(false);
    }

    const liveItemEndTime = auctionItemBidsLive?.find((entry: any) => String(entry.itemId) === String(item.itemId))?.itemEndTime;

    let parseItemEndTime = liveItemEndTime ? parseDate(liveItemEndTime) : 0;
    let parseBidMatchItemEndTime = bidsData?.itemEndTime ? parseDate(bidsData?.itemEndTime) : 0;

    const updatedItemEndTime = (parseItemEndTime >= parseBidMatchItemEndTime ? liveItemEndTime : bidsData?.itemEndTime) || item.itemEndTime
    const itemEndTime = updatedItemEndTime

    setAuctionItem((prev) => ({
      ...prev,
      itemEndTime: itemEndTime,
      itemHasEnded: checkIfAuctionItemEnded(itemEndTime, serverTime),
      itemHasBeenSynced: true,
    }));

    return true;
  }

  const { isLoading: bidIsLoading } = useSWR(
    fetchKey,
    auctionItemFetcher,
    {
      refreshInterval: auctionHasEnded ? 0 : REVALIDATE_AUCTION_ITEM_TIME,
      revalidateOnFocus: true,
      revalidateIfStale: true,
      revalidateOnMount: true,
    }
  );

  const visibleElements =
    serverTime && (item as any)?.itemHasBeenSynced;

  const actualBidsData = bidData ?? cachedBids;

  const leadingAutoBid = bidData?.leadingAutoBid;
  const isLeadingAutoBid =
    !!user?.id && Number(leadingAutoBidUpdated?.autoUserId) === Number(user?.id);

  // Use media array if available (new format with mixed sorting)
  // Otherwise fall back to separate images array (old format)
  const hasMedia = item.media && item.media.length > 0;

  const cardImages = !hasMedia && item.images
    ? Object.values(item.images).map((image: any) => {
        return image;
      })
    : [];

  const isWinner =
    user && bidData?.bids ? item?.itemWinningUserId === user?.id : false;

  const leadingBid = bidData?.bids
    ? bidData?.bids[0]?.historyBidValue
    : item.itemMinBid;
    
    const currentMaxBid = bidData?.bids[0]?.historyBidValue || item.itemMinBid;

  const savedPage = useMemo(() => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem(`auctionPage-${auction.partId}`) || "1";
    }
    return "1";
  }, [auction.partId]);

  const auctionLink = getAuctionLink(auction);

  const calculateTimeRemaining = useCallback(() => {
    const endTimeStr = parseDate(item.itemEndTime);
    const now = Date.now();
    return endTimeStr - now;
  }, [item.itemEndTime]);

  const isEnding = calculateTimeRemaining() <= BID_TIME_REMAINING_COUNTDOWN;

  const timeContainerClass = classNames(
    "block md:hidden bottom-0 relative w-full py-2 text-white text-xs bg-toast-blue text-white z-[500] rounded-b-lg",
    {
      "!bg-danger !text-white": isEnding && !hasEnded,
    }
  );

  const formatCollectShowObject = (data: any) => {
    return data?.text || "Kontakta oss för mer information";
  };

  const MarkerSvg = () => (
    <svg
      version="1.1"
      id="_x32_"
      xmlns="http://www.w3.org/2000/svg"
      width="16px"
      height="16px"
      viewBox="0 0 512 512"
      fill="currentColor"
    >
      <g>
        <path
          d="M256.016,0C158.797,0.031,80.094,78.781,80.063,175.953c0.063,14.297,3.031,28.641,7.563,43.797
  c7.969,26.438,21.094,55.328,36.281,84.547c45.563,87.359,110.328,177.391,110.688,177.891L256.016,512l21.391-29.813
  c0.25-0.313,37.969-52.844,76.016-116.266c19.016-31.766,38.141-66.25,52.828-98.859c7.344-16.313,13.578-32.172,18.156-47.313
  c4.531-15.156,7.469-29.5,7.531-43.797C431.906,78.781,353.203,0.031,256.016,0z M373.938,204.594
  c-6.344,21.156-18.25,47.906-32.594,75.359c-21.484,41.266-48.281,84.375-69.625,116.953c-5.719,8.719-10.969,16.609-15.703,23.594
  c-14.891-22-35.594-53.594-55.844-87.75c-17.719-29.906-35.063-61.75-47.656-90.25c-6.297-14.188-11.391-27.547-14.781-39.094
  c-3.422-11.5-5-21.281-4.953-27.453c0.016-34.109,13.75-64.734,36.078-87.156c22.391-22.328,53.016-36.063,87.156-36.094
  c34.109,0.031,64.75,13.766,87.125,36.094c22.359,22.422,36.078,53.047,36.094,87.156
  C379.281,182.344,377.594,192.563,373.938,204.594z"
        />
        <path
          d="M256.016,118.719c-31.594,0-57.219,25.641-57.219,57.234c0,31.609,25.625,57.219,57.219,57.219
  c31.578,0,57.219-25.609,57.219-57.219C313.234,144.359,287.594,118.719,256.016,118.719z"
        />
      </g>
    </svg>
  );

  const VehicleDataTable = useMemo(
    () =>
      React.memo(function VehicleDataTable({ vehicleData }: any) {
        const placeholder = "-";

        return (
          <div className="bg-gray-50 p-5 px-7 mt-8 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Fordonsinformation</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div>
                  <strong>Reg.nr:</strong>{" "}
                  {vehicleData?.vehicleLicencePlate || placeholder}
                </div>
                <div>
                  <strong>Modell:</strong>{" "}
                  {vehicleData?.vehicleModel || placeholder}
                </div>
                <div>
                  <strong>Växellåda:</strong>{" "}
                  {vehicleData?.vehicleTransmission || placeholder}
                </div>
                <div>
                  <strong>Färg:</strong>{" "}
                  {vehicleData?.vehicleColor || placeholder}
                </div>
                <div>
                  <strong>Längd (mm):</strong>{" "}
                  {vehicleData?.vehicleLength || placeholder}
                </div>
                <div>
                  <strong>Effekt (kW):</strong>{" "}
                  {vehicleData?.vehicleOutput || placeholder}
                </div>
                <div>
                  <strong>Antal brukare:</strong>{" "}
                  {vehicleData?.vehicleOwnerCount || placeholder}
                </div>
                <div>
                  <strong>Körförbud:</strong>{" "}
                  {vehicleData?.vehicleProhibitedForTraffic === "no"
                    ? "Nej"
                    : vehicleData?.vehicleProhibitedForTraffic === "yes"
                    ? "Ja"
                    : placeholder}
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <strong>Märke:</strong>{" "}
                  {vehicleData?.vehicleBrand || placeholder}
                </div>
                <div>
                  <strong>Modellår:</strong>{" "}
                  {vehicleData?.vehicleModelYear || placeholder}
                </div>
                <div>
                  <strong>Drivmedel:</strong>{" "}
                  {vehicleData?.vehicleFuel || placeholder}
                </div>
                <div>
                  <strong>Registrerad:</strong>{" "}
                  {vehicleData?.vehicleInspectionDate || placeholder}
                </div>
                <div>
                  <strong>Bredd (mm):</strong>{" "}
                  {vehicleData?.vehicleWidth || placeholder}
                </div>
                <div>
                  <strong>Cylindervolym (cm³):</strong>{" "}
                  {vehicleData?.vehicleCubicCapacity || placeholder}
                </div>
                <div>
                  <strong>Godkänd besiktning:</strong>{" "}
                  {vehicleData?.vehicleInspectionApprovedDate || placeholder}
                </div>
                <div>
                  <strong>Skatt:</strong>{" "}
                  {vehicleData?.vehicleAnnualTax
                    ? `${vehicleData.vehicleAnnualTax} SEK`
                    : placeholder}
                </div>
              </div>
            </div>
          </div>
        );
      }),
    []
  );

  const FilesTable = useMemo(
    () =>
      React.memo(function FilesTable({ files }: any) {
        if (!files || files.length === 0) return null;

        return (
          <div className="mt-6 md:my-6 bg-gray-50 p-5 px-7 rounded-lg">
            <h3 className="text-xl font-semibold mt-2 mb-2">Filer</h3>
            <ul className="list-disc list-inside text-base">
              {files.map((file: any, index: any) => {
                const fileExtension = file.url.split(".").pop() || "unknown";
                return (
                  <li key={index}>
                    <a
                      href={file.url}
                      download={file.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="!no-underline font-semibold"
                    >
                      {file.name}{" "}
                      <span className="font-light">({fileExtension})</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      }),
    []
  );

  return (
    <>
      <ActiveAuctionTimeSync isActive={!auctionHasEnded} />
      <div className="py-4 px-5 bg-gray-50 rounded-lg md:-mt-6 mb-5 flex flex-row justify-between">
        <Link
          href={`${auctionLink}?sida=${savedPage}`}
          className="!no-underline"
        >
          <div className="items-center flex flex-row space-x-2 text-base md:text-lg hover:text-blue cursor-pointer">
            <svg
              fill="#004a97"
              version="1.1"
              id="Capa_1"
              xmlns="http://www.w3.org/2000/svg"
              width="26px"
              height="26px"
              viewBox="0 0 299.021 299.021"
            >
              <g>
                <g>
                  <path
                    d="M292.866,254.432c-2.288,0-4.443-1.285-5.5-3.399c-0.354-0.684-28.541-52.949-146.169-54.727v51.977
            c0,2.342-1.333,4.48-3.432,5.513c-2.096,1.033-4.594,0.793-6.461-0.63L2.417,154.392C0.898,153.227,0,151.425,0,149.516
            c0-1.919,0.898-3.72,2.417-4.888l128.893-98.77c1.87-1.426,4.365-1.667,6.461-0.639c2.099,1.026,3.432,3.173,3.432,5.509v54.776
            c3.111-0.198,7.164-0.37,11.947-0.37c43.861,0,145.871,13.952,145.871,143.136c0,2.858-1.964,5.344-4.75,5.993
            C293.802,254.384,293.34,254.432,292.866,254.432z"
                  />
                </g>
              </g>
            </svg>
            <span>
              Del av: <strong>{auction.auctionTitle}</strong>
            </span>
          </div>
        </Link>

        <div className="hidden md:block">
          <TopbarAuctionPagination
            sortedItems={sortedItemsForPagination}
            currentItemSortNo={item.itemSortNo}
            getAuctionItemLink={(targetItem: any) =>
              getAuctionItemLink(targetItem)
            }
          />{" "}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 md:gap-5 mb-8">
        <div className="grid-cols-1 md:col-span-8 xl:col-span-8">
          <div className="grid grid-cols-1 md:grid-cols-12 md:gap-8 w-full items-stretch">
            <div className="w-full col-span-12 md:col-span-6 relative h-full">
              <FavoriteButton auctionItemFavorite={auctionItemFavorite} />
              {hasMedia || cardImages.length > 0 ? (
                <GallerySlider
                  media={hasMedia ? item.media : undefined}
                  images={!hasMedia ? cardImages : undefined}
                  includeVideos
                  isArchived={auctionHasEnded}
                >
                  {visibleElements && (
                    <div className={timeContainerClass}>
                      <div className="container">
                        <BidCountDown
                          item={item}
                          hasEnded={hasEnded}
                          isWinner={isWinner}
                          bidData={bidData}
                          serverTime={serverTime}
                          noCountDownTextColor
                        />{" "}
                      </div>
                    </div>
                  )}
                </GallerySlider>
              ) : (
                <div className="relative">
                  <NoImageFound containerClass="relative w-full rounded-lg object-cover overflow-hidden shrink-0 hover:scale-115 transition-all max-h-[400px] duration-1000 cursor-zoom-in select-none mb-6 !h-[270px] relative md:!h-full md:!min-h-[350px]" />
                  {visibleElements && (
                    <div className="absolute bottom-0 w-full">
                      <div className={timeContainerClass}>
                        <div className="container">
                          <BidCountDown
                            item={item}
                            hasEnded={hasEnded}
                            isWinner={isWinner}
                            bidData={bidData}
                            serverTime={serverTime}
                            noCountDownTextColor
                          />{" "}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="col-span-6 flex flex-col items-start justify-start box-border h-full">
              <div className="self-stretch flex flex-col items-start justify-start">
                <span className="text-2xl md:text-32 self-stretch relative leading-1 font-semibold text-blue md:mt-0">
                  <Link href={itemLink} className="!no-underline">
                    Rop {item.itemSortNo}:{" "}
                    <h1 className="inline text-2xl md:text-32 self-stretch relative md:leading-10 font-semibold text-blue">
                      {item.itemTitle}
                    </h1>
                  </Link>
                </span>
                {item?.address?.addressTitle && (
                  <div className="self-stretch relative leading-1 font-normal text-paragraph text-base mt-2 md:text-1xl flex flex-row space-x-2 items-center">
                    <span className="text-muted-bg">
                      <MarkerSvg />
                    </span>
                    <span>{item?.address?.addressTitle}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col space-y-8 xl:space-x-10 md:justify-between xl:flex-row w-full items-start mt-8">
                <div className="flex flex-col w-full flex-grow">
                  <div className="self-stretch relative text-sm text-black">
                    <div className="flex-1 flex flex-col items-start justify-start">
                      <div className="self-stretch grid grid-cols-1 items-start justify-start gap-[20px] space-y-6">
                        {visibleElements ? (
                          <div className="flex flex-col self-stretch relative leading-1 font-semibold text-base">
                            <span className="font-normal">
                              {hasEnded
                                ? "Slutpris"
                                : actualBidsData &&
                                  actualBidsData?.bids?.length > 0
                                ? "Ledande bud"
                                : "Utgångspris"}
                              :{" "}
                            </span>
                            <span className="text-xxl">
                              <CurrentBid
                                auctionItem={item}
                                bids={actualBidsData?.bids}
                                showLeadingUser
                              />
                            </span>
                          </div>
                        ) : (
                          <div className="self-stretch relative leading-[150%]">
                            <Skeleton height={30} />
                          </div>
                        )}
                      </div>
                      <div className="relative mt-3 md:hdidden leading-1 text-xs flex space-y-2 flex-col rounded-xl w-full md:w-fit h-fit my-6 mb-0 md:my-0">
                        <div className="self-stretch relative leading-1">
                          <span className="font-semibold">Moms:</span>{" "}
                          {item.itemVatValue}%
                        </div>
                        <div className="self-stretch relative leading-1">
                          <span className="font-semibold">Slagavgift:</span>{" "}
                          {formatPrice(item.itemFeeValue)} exkl. moms
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="md:hidden flex flex-col items-start justify-start w-full gap-[24px] text-paragraph bg-aliceblue px-6 py-8 rounded-lg">
                  {visibleElements ? (
                    <BidCountDown
                      item={item}
                      hasEnded={hasEnded}
                      bidData={actualBidsData}
                      serverTime={serverTime}
                      isWinner={isWinner}
                    />
                  ) : (
                    <div className="self-stretch relative leading-[150%]">
                      <Skeleton
                        height={30}
                        customHighlightBackground="#fff"
                        baseColor="#e8eff5"
                      />
                    </div>
                  )}
                  <AuctionBidFieldMemomized
                    leadingAutoBid={leadingAutoBidUpdated}
                    hasEnded={hasEnded}
                    auctionItem={item}
                    leadingBid={leadingBid}
                    layout="vertical"
                    isLeadingAutoBid={isLeadingAutoBid}
                  />

                  <div className="bg-aliceblue w-full rounded-lg">
                    <AuctionBidHistory
                      auctionItem={item}
                      user={user}
                      hasEnded={hasEnded}
                      bidData={actualBidsData}
                      bidIsLoading={bidIsLoading}
                      showAllBids={showAllBids}
                      maxBidsDisplayed={MAX_BIDS_FETCHED}
                      setSHowAllBids={setSHowAllBids}
                      extraClass={classNames({
                        "max-h-[300px] overflow-y-auto": true,
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {item?.itemDescription && (
            <div className="mt-6 md:my-6 bg-gray-50 p-5 px-7 rounded-lg">
              <h3 className="text-xl font-semibold mt-2 mb-2">
                Mer information om ropet
              </h3>
              <div className="self-stretch relative leading-[150%]">
                <ReadMoreArea
                  expandLabel="Läs mer"
                  collapseLabel="Läs mindre"
                  lettersLimit={100}
                >
                  {item.itemDescription}
                </ReadMoreArea>
              </div>
            </div>
          )}

          <div className="mt-6 md:my-6 p-5 px-7 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-semibold mt-2 mb-2">Detaljer</h3>
            <div className="relative leading-1 text-profile-gray-dark flex-grow rounded-lg text-slate-gray-dark py-4 text-14 flex space-y-2 flex-col w-auto w-full !mt-0 md:col-span-8">
              <div className="self-stretch relative leading-[150%]">
                <span className="font-semibold">Utgångspris:</span>{" "}
                {formatPrice(item.itemMinBid)}
              </div>
              <div className="self-stretch relative leading-[150%]">
                <span className="font-semibold">Moms:</span> {item?.itemVatValue && item?.itemVatValue >= 1 ? `${item.itemVatValue}% tillkommer` : `${item?.itemVatValue}%`}
              </div>
              <div className="self-stretch relative leading-[150%]">
                <span className="font-semibold">Slagavgift:</span>{" "}
                {formatPrice(item.itemFeeValue)} exkl. moms
              </div>
              <div className="self-stretch relative leading-1 hidden md:block">
                <span className="font-semibold">Visning:</span>{" "}
                {formatCollectShowObject(item.itemShowingInfo)}
              </div>
              <div className="self-stretch relative leading-1 hidden md:block">
                <span className="font-semibold">Avhämtning:</span>{" "}
                {formatCollectShowObject(item.itemCollectInfo)}
              </div>
            </div>
          </div>

          {item?.vehicleData && (
            <VehicleDataTable vehicleData={item.vehicleData} />
          )}

          {item?.files && <FilesTable files={item?.files} />}

          <AuctionItemFAQs
            auctionItem={item}
            auction={auction}
            themeSettings={themeSettings}
          />
        </div>

        <div className="items-start md:col-span-4 xl:col-span-4 md:h-auto md:max-h-fit flex flex-col md:flex-col space-y-6 space-x-5 order-0 md:order-1 m-0">
          <div className="rounded-lg md:bg-aliceblue w-full">
          <div
            className={classNames(
              "bg-light-grey no-scrollbar text-profile-gray-dark flex-grow flex flex-col md:flex-row space-x-6 items-start justify-between box-border duration-300 ease-in-out max-h-fit",
              {
                "overflow-scroll": showAllBids,
              }
            )}
          >
            <div className="hidden w-full md:flex flex-col items-start justify-start p-6 box-border gap-[24px] text-paragraph">
              {visibleElements ? (
                <BidCountDown
                  item={item}
                  hasEnded={hasEnded}
                  bidData={actualBidsData}
                  isWinner={isWinner}
                  serverTime={serverTime}
                />
              ) : (
                <div className="self-stretch relative leading-[150%]">
                  <Skeleton height={30} baseColor="#e8eff5" />
                </div>
              )}
              <AuctionBidFieldMemomized
                leadingAutoBid={leadingAutoBidUpdated}
                hasEnded={hasEnded}
                auctionItem={item}
                leadingBid={leadingBid}
                layout="horizontal"
                isLeadingAutoBid={isLeadingAutoBid}
              />
              <AuctionBidHistory
                auctionItem={item}
                user={user}
                hasEnded={hasEnded}
                bidData={actualBidsData}
                showAllBids={showAllBids}
                setSHowAllBids={setSHowAllBids}
                bidIsLoading={bidIsLoading}
                maxBidsDisplayed={MAX_BIDS_FETCHED}
                extraClass={classNames({
                  "max-h-[300px] overflow-y-auto": true,
                })}
              />
            </div>

          </div>
        </div>
          {currentMaxBid >= 100000 && (
            <>
              {((user?.id && userInfo?.userType === "company") || !user?.id) && (
              <div className="bg-white rounded-lg !ml-0 shadow-lg p-6 mt-8 w-full flex flex-col items-center space-y-4">
                  <div className="text-center space-y-2">
                    <p className="text-base font-semibold text-gray-800">
                        Finansiering är möjligt för företag på detta objekt
                      </p>
                    <p className="text-sm text-gray-600">
                        <>
                         Kontakta oss på telefon: <a href="tel:0346-48770"  className="text-blue hover:underline font-semibold">0346-487 70</a> eller e-post: <a href="mailto:finansiering@tovek.se" className="text-blue hover:underline font-semibold">finansiering@tovek.se</a> 
                         <div className="mt-8 text-center">
                          <Link href="/finansiering" className="!no-underline" target="_blank">
                            <Button
                             bgColor="transparent"
                             borderColor="blue"
                             className="mx-auto"
                             textColor="blue"
                            >
                              Läs mer om finansiering
                            </Button>
                          </Link>
                        </div>
                        </>
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const AuctionBidFieldMemomized = React.memo(function AuctionBidFieldMemomized({
  layout,
  hasEnded,
  auctionItem,
  leadingBid,
  isLeadingAutoBid,
  leadingAutoBid,
}: {
  layout: "horizontal" | "vertical";
  hasEnded: boolean;
  auctionItem: AuctionItem;
  leadingBid: number;
  isLeadingAutoBid: boolean;
  leadingAutoBid: any;
}) {
  return (
    <AuctionBidField
      layout={layout}
      leadingAutoBid={leadingAutoBid}
      isLeadingAutoBid={isLeadingAutoBid}
      hasEnded={hasEnded}
      auctionItem={auctionItem}
      leadingBid={leadingBid}
    />
  );
});

AuctionItemClient.displayName = "AuctionItemClient";

export default AuctionItemClient;
